/**
 * DisqualificationContext
 * ----------------------
 * Sprint 04-30 P1. Team-shared "rejected" tender list. Any user's DQ hides
 * the tender from search for everyone.
 *
 * Intentionally simpler than BookmarkContext:
 *  - No localStorage cache. The list is small (<<10k), backend-only is fine.
 *  - No reconcile flow. Single-source-of-truth = ES.
 *  - GET returns the team-wide list, not just the current user's.
 *
 * Mutual exclusivity with bookmarks is enforced at the backend (creating a
 * DQ deletes the user's bookmark for that tender, and vice versa).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { disqualificationsAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { logEvent } from '../utils/eventLog';

const DisqualificationContext = createContext();

export const useDisqualifications = () => {
  const ctx = useContext(DisqualificationContext);
  if (!ctx) throw new Error('useDisqualifications must be used within DisqualificationProvider');
  return ctx;
};

const normalize = (d) => ({
  ...d,
  id: d.tender_id || d.id,
  tenderId: d.tender_id || d.id,
  tender_id: d.tender_id || d.id,
  disqualifiedAt: d.disqualified_at || d.disqualifiedAt,
  closingDate: d.closing_date || d.closingDate,
});

export const DisqualificationProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await disqualificationsAPI.list();
      setItems((list || []).map(normalize));
      setError(null);
    } catch (e) {
      console.error('Error loading disqualifications:', e);
      setError(e?.response?.status === 401 ? 'unauthorized' : 'load-failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load only once auth has resolved AND we have a token, then re-run whenever
  // auth state changes. The old code fetched on mount unconditionally, so the
  // request went out before App.js had written the Auth0 token to
  // localStorage → 401 → stuck on "please sign in" even when authenticated
  // (the effect never re-ran). We ensure the token is set here (App.js sets it
  // too, but child effects run before the parent's) before fetching.
  useEffect(() => {
    if (authLoading) return; // wait for Auth0 to resolve
    if (!isAuthenticated) {
      setItems([]);
      setError('unauthorized');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        if (token) localStorage.setItem('authToken', token);
      } catch (_) { /* dev mode / silent-auth unavailable — the interceptor falls back to any stored token */ }
      if (!cancelled) refresh();
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, authLoading, getAccessTokenSilently, refresh]);

  const isDisqualified = useCallback(
    (tenderId) => items.some((d) => d.tender_id === tenderId),
    [items],
  );

  const disqualify = useCallback(async (tender) => {
    try {
      const created = await disqualificationsAPI.disqualify(tender);
      setItems((prev) => {
        // de-dupe in case someone else also disqualified this tender
        const filtered = prev.filter((d) => d.tender_id !== (tender.tender_id || tender.id));
        return [normalize(created), ...filtered];
      });
      logEvent('disqualification.created', {
        tender_id: tender.tender_id || tender.id,
        meta: { reason: tender.reason || null, title: tender.title },
      });
      return created;
    } catch (e) {
      console.error('Error disqualifying tender:', e);
      throw e;
    }
  }, []);

  const undisqualify = useCallback(async (tenderId) => {
    try {
      await disqualificationsAPI.remove(tenderId);
      setItems((prev) => prev.filter((d) => d.tender_id !== tenderId));
    } catch (e) {
      console.error('Error removing disqualification:', e);
      throw e;
    }
  }, []);

  return (
    <DisqualificationContext.Provider
      value={{
        disqualifications: items,
        loading,
        error,
        isDisqualified,
        disqualify,
        undisqualify,
        refresh,
      }}
    >
      {children}
    </DisqualificationContext.Provider>
  );
};
